import React, { useState } from 'react';
import type { CreateJobRequest, Job, SourceSystem } from '@atlas/shared';
import {
  SOURCE_SYSTEMS,
  SOURCE_SYSTEM_LABELS,
  buildS3Prefix,
  isUrlIntakeSource,
} from '@atlas/shared';
import { createJob } from '../lib/callables.js';
import { confettiEverywhere } from '../lib/confetti.js';
import { SmartFill } from './SmartFill.js';

interface NewImportFormProps {
  onCreated?: (jobId: string) => void;
  /**
   * Existing jobs, used to warn (before submit) when the client name would
   * collide with another import's S3 folder for the same source system.
   */
  existingJobs?: Array<Job & { id: string }>;
}

/**
 * Local form state always keeps the full FTP block populated (even for
 * URL-intake sources, where it's simply ignored on submit) so the field
 * bindings stay simple. The submitted CreateJobRequest is derived in
 * handleSubmit and only carries the fields relevant to the chosen intake mode.
 */
type FormState = Omit<CreateJobRequest, 'ftp' | 'sourceUrl'> & {
  ftp: NonNullable<CreateJobRequest['ftp']>;
  sourceUrl: string;
};

const DEFAULT: FormState = {
  clientName: '',
  // Empty until the engineer picks one; the <select> is `required` so the form
  // cannot be submitted without a valid choice.
  sourceSystem: '' as SourceSystem,
  sourceUrl: '',
  ftp: {
    host: '',
    port: 21,
    username: '',
    password: '',
    path: '/',
    protocol: 'ftp',
  },
};

export function NewImportForm({ onCreated, existingJobs = [] }: NewImportFormProps) {
  const [form, setForm] = useState<FormState>(DEFAULT);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The S3 folder is <sourceSystem>/<slug(client)>/files, so a name collides
  // only once both fields are set. Compare on the computed prefix so slug
  // equivalents (e.g. "Acme Corp" vs "acme_corp") are caught too. A `deleted`
  // job has already had that folder wiped, so the name can be reused. Jobs
  // still wiping (`deleting`) or whose wipe failed still hold the path.
  // The backend rejects duplicates as the source of truth; this is an early warning.
  const nameTaken =
    Boolean(form.clientName.trim()) &&
    Boolean(form.sourceSystem) &&
    existingJobs.some(
      (j) =>
        j.status !== 'deleted' &&
        j.s3Destination?.prefix ===
          buildS3Prefix(form.clientName, form.sourceSystem),
    );

  function set<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  function setFtp<K extends keyof FormState['ftp']>(
    key: K,
    value: FormState['ftp'][K],
  ) {
    setForm((f) => ({ ...f, ftp: { ...f.ftp, [key]: value } }));
  }

  // Switching protocol updates the port to its default (21/990/22), but only
  // if the user hasn't typed a custom, non-default port.
  function handleProtocolChange(protocol: FormState['ftp']['protocol']) {
    const defaultPort: Record<FormState['ftp']['protocol'], number> = {
      ftp: 21,
      ftps: 990,
      sftp: 22,
    };
    setForm((f) => {
      const isDefaultPort = [21, 990, 22].includes(f.ftp.port);
      return {
        ...f,
        ftp: {
          ...f.ftp,
          protocol,
          port: isDefaultPort ? defaultPort[protocol] : f.ftp.port,
        },
      };
    });
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (nameTaken) {
      setError('This name is already taken for this source system, please choose another.');
      return;
    }
    setError(null);
    setLoading(true);
    const startedAt = Date.now();
    try {
      // Build a request that only carries the fields for the chosen intake mode:
      // URL-intake sources (e.g. Loxo) send `sourceUrl` and no FTP block; every
      // other source sends the FTP block and no URL.
      const urlIntake = isUrlIntakeSource(form.sourceSystem);
      const request: CreateJobRequest = urlIntake
        ? {
            clientName: form.clientName,
            sourceSystem: form.sourceSystem,
            sourceUrl: form.sourceUrl.trim(),
            // URL sources (Loxo) deliver an AES-256 encrypted zip.
            ...(form.zipPassword ? { zipPassword: form.zipPassword } : {}),
          }
        : {
            clientName: form.clientName,
            sourceSystem: form.sourceSystem,
            ftp: form.ftp,
            ...(form.certPassword !== undefined
              ? { certPassword: form.certPassword }
              : {}),
            ...(form.zipPassword !== undefined
              ? { zipPassword: form.zipPassword }
              : {}),
          };
      const result = await createJob(request);
      // Keep the skeleton on screen long enough to actually register, even when
      // the call returns near-instantly.
      const MIN_SKELETON_MS = 900;
      const remaining = MIN_SKELETON_MS - (Date.now() - startedAt);
      if (remaining > 0) {
        await new Promise((resolve) => setTimeout(resolve, remaining));
      }
      // Celebrate a successfully-filed import with a screen-wide confetti rain.
      confettiEverywhere();
      setForm(DEFAULT);
      onCreated?.(result.data.jobId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }

  function handleSmartFill(partial: Parameters<React.ComponentProps<typeof SmartFill>['onFill']>[0]) {
    setForm((f) => ({
      ...f,
      clientName: partial.clientName ?? f.clientName,
      ...(partial.sourceSystem !== undefined ? { sourceSystem: partial.sourceSystem } : {}),
      ...(partial.sourceUrl !== undefined ? { sourceUrl: partial.sourceUrl } : {}),
      ...(partial.certPassword !== undefined ? { certPassword: partial.certPassword } : {}),
      ...(partial.zipPassword !== undefined ? { zipPassword: partial.zipPassword } : {}),
      ftp: {
        ...f.ftp,
        ...(partial.ftp ?? {}),
      },
    }));
  }

  const isBullhorn = form.sourceSystem === 'bullhorn';
  const isThrive = form.sourceSystem === 'thrive';
  const isUrlIntake = isUrlIntakeSource(form.sourceSystem);

  // While the import is being filed, swap the fields for a skeleton so the
  // form reads as "working" without losing its footprint/layout.
  if (loading) {
    return <NewImportFormSkeleton />;
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      {/* Smart fill */}
      <SmartFill onFill={handleSmartFill} />

      {/* Row 1: Client name + source system */}
      <div className="flex gap-4">
        <label className="flex-1 flex flex-col gap-1">
          <span className="text-xs font-medium text-[#73726e]">
            Client name
          </span>
          <input
            required
            className={`px-2.5 py-1.5 text-sm border rounded focus:outline-none focus:ring-1 text-[#37352f] placeholder:text-[#aeaaa4] ${
              nameTaken
                ? 'border-[#e03e3e] focus:ring-[#e03e3e]'
                : 'border-[#e9e9e7] focus:ring-[#2383e2]'
            }`}
            placeholder="e.g. acme-corp"
            value={form.clientName}
            onChange={(e) => set('clientName', e.target.value)}
          />
          {nameTaken && (
            <span className="text-xs text-[#e03e3e]">
              This name is already taken for this source system, please choose another.
            </span>
          )}
        </label>

        <label className="flex-1 flex flex-col gap-1">
          <span className="text-xs font-medium text-[#73726e]">
            Source system
          </span>
          <select
            required
            className="px-2.5 py-1.5 text-sm border border-[#e9e9e7] rounded focus:outline-none focus:ring-1 focus:ring-[#2383e2] text-[#37352f] bg-white"
            value={form.sourceSystem}
            onChange={(e) => set('sourceSystem', e.target.value as SourceSystem)}
          >
            <option value="" disabled>
              Select source system…
            </option>
            {SOURCE_SYSTEMS.map((s) => (
              <option key={s} value={s}>
                {SOURCE_SYSTEM_LABELS[s]}
              </option>
            ))}
          </select>
        </label>
      </div>

      {/* URL-intake sources (e.g. Loxo): a single download link, no FTP. */}
      {isUrlIntake ? (
        <div className="flex flex-col gap-2 rounded border border-[#d7eafb] bg-[#f3f9ff] px-3 py-2.5">
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-[#73726e]">
              Source download URL
            </span>
            <input
              required
              type="url"
              autoComplete="off"
              className="px-2.5 py-1.5 text-sm border border-[#e9e9e7] rounded focus:outline-none focus:ring-1 focus:ring-[#2383e2] text-[#37352f] placeholder:text-[#aeaaa4] font-mono"
              placeholder="https://…/export.zip?X-Amz-Signature=…"
              value={form.sourceUrl}
              onChange={(e) => set('sourceUrl', e.target.value)}
            />
            <span className="text-xs text-[#2383e2]">
              {SOURCE_SYSTEM_LABELS[form.sourceSystem as SourceSystem] ?? 'This source'} delivers a download link (a presigned .zip) instead of FTP — paste it here and we'll fetch it straight into S3.
            </span>
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-[#73726e]">
              Zip password
            </span>
            <input
              required
              type="password"
              autoComplete="off"
              className="px-2.5 py-1.5 text-sm border border-[#e9e9e7] rounded focus:outline-none focus:ring-1 focus:ring-[#2383e2] text-[#37352f] placeholder:text-[#aeaaa4]"
              placeholder="Password for the encrypted export zip"
              value={form.zipPassword ?? ''}
              onChange={(e) =>
                set('zipPassword', e.target.value === '' ? undefined : e.target.value)
              }
            />
            <span className="text-xs text-[#2383e2]">
              The export is AES-256 encrypted — the password (provided by {SOURCE_SYSTEM_LABELS[form.sourceSystem as SourceSystem] ?? 'the source'}) is needed to unpack it after download.
            </span>
          </label>
        </div>
      ) : (
        <>
          {/* Row 2: FTP details */}
          <div className="flex gap-3">
            <label className="flex-1 flex flex-col gap-1">
              <span className="text-xs font-medium text-[#73726e]">FTP host</span>
              <input
                required
                className="px-2.5 py-1.5 text-sm border border-[#e9e9e7] rounded focus:outline-none focus:ring-1 focus:ring-[#2383e2] text-[#37352f] placeholder:text-[#aeaaa4]"
                placeholder="ftp.client.com"
                value={form.ftp.host}
                onChange={(e) => setFtp('host', e.target.value)}
              />
            </label>

            <label className="w-24 flex flex-col gap-1">
              <span className="text-xs font-medium text-[#73726e]">Port</span>
              <input
                required
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                className="px-2.5 py-1.5 text-sm border border-[#e9e9e7] rounded focus:outline-none focus:ring-1 focus:ring-[#2383e2] text-[#37352f]"
                value={form.ftp.port === 0 ? '' : String(form.ftp.port)}
                onChange={(e) => {
                  const raw = e.target.value.replace(/\D/g, '');
                  setFtp('port', raw === '' ? 0 : parseInt(raw, 10));
                }}
              />
            </label>

            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-[#73726e]">Protocol</span>
              <select
                className="px-2.5 py-1.5 text-sm border border-[#e9e9e7] rounded focus:outline-none focus:ring-1 focus:ring-[#2383e2] text-[#37352f] bg-white"
                value={form.ftp.protocol}
                onChange={(e) =>
                  handleProtocolChange(e.target.value as FormState['ftp']['protocol'])
                }
              >
                <option value="ftp">FTP</option>
                <option value="ftps">FTPS</option>
                <option value="sftp">SFTP</option>
              </select>
            </label>
          </div>

          {/* Row 3: credentials */}
          <div className="flex gap-3">
            <label className="flex-1 flex flex-col gap-1">
              <span className="text-xs font-medium text-[#73726e]">Username</span>
              <input
                required
                autoComplete="off"
                className="px-2.5 py-1.5 text-sm border border-[#e9e9e7] rounded focus:outline-none focus:ring-1 focus:ring-[#2383e2] text-[#37352f] placeholder:text-[#aeaaa4]"
                placeholder="ftpuser"
                value={form.ftp.username}
                onChange={(e) => setFtp('username', e.target.value)}
              />
            </label>

            <label className="flex-1 flex flex-col gap-1">
              <span className="text-xs font-medium text-[#73726e]">Password</span>
              <input
                required
                type="password"
                autoComplete="new-password"
                className="px-2.5 py-1.5 text-sm border border-[#e9e9e7] rounded focus:outline-none focus:ring-1 focus:ring-[#2383e2] text-[#37352f]"
                value={form.ftp.password}
                onChange={(e) => setFtp('password', e.target.value)}
              />
            </label>
          </div>
        </>
      )}

      {/* Bullhorn source → certificate password (auto-decrypt on the VM) */}
      {isBullhorn && (
        <div className="flex flex-col gap-2 rounded border border-[#ece5f3] bg-[#f9f6fc] px-3 py-2.5">
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-[#73726e]">
              Certificate password{' '}
              <span className="font-normal text-[#aeaaa4]">(optional)</span>
            </span>
            <input
              type="password"
              autoComplete="off"
              className="px-2.5 py-1.5 text-sm border border-[#e9e9e7] rounded focus:outline-none focus:ring-1 focus:ring-[#9065b0] text-[#37352f] placeholder:text-[#aeaaa4]"
              placeholder="Leave blank to use the reversed database name"
              value={form.certPassword ?? ''}
              onChange={(e) =>
                set('certPassword', e.target.value === '' ? undefined : e.target.value)
              }
            />
            <span className="text-xs text-[#9065b0]">
              Bullhorn exports are encrypted — the backup is auto-decrypted on the migration VM after transfer.
            </span>
          </label>
        </div>
      )}

      {/* Thrive source → password-protected zip (auto-extracted after transfer) */}
      {isThrive && (
        <div className="flex flex-col gap-2 rounded border border-[#d7eafb] bg-[#f3f9ff] px-3 py-2.5">
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-[#73726e]">
              Zip password
            </span>
            <input
              type="password"
              autoComplete="off"
              className="px-2.5 py-1.5 text-sm border border-[#e9e9e7] rounded focus:outline-none focus:ring-1 focus:ring-[#2383e2] text-[#37352f] placeholder:text-[#aeaaa4]"
              placeholder="Password for the Thrive zip archive"
              value={form.zipPassword ?? ''}
              onChange={(e) =>
                set('zipPassword', e.target.value === '' ? undefined : e.target.value)
              }
            />
            <span className="text-xs text-[#2383e2]">
              Thrive delivers a password-protected zip — it's extracted automatically after transfer, then the dump is restored.
            </span>
          </label>
        </div>
      )}

      {error && (
        <p className="text-sm text-[#e03e3e] bg-[#fbe8e8] border border-[#f5c6c6] rounded px-3 py-2">
          {error}
        </p>
      )}

      <div className="flex justify-end pt-1">
        <button
          type="submit"
          disabled={loading || nameTaken}
          className="px-4 py-1.5 text-sm font-medium text-white bg-[#37352f] rounded hover:bg-[#2e2e2c] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {loading ? 'Starting…' : 'Start import'}
        </button>
      </div>
    </form>
  );
}

/** Animated placeholder shown while a new import is being filed. */
function NewImportFormSkeleton() {
  const bar = 'animate-pulse rounded bg-[#ebebe9]';

  const Field = ({ width = 'flex-1', labelW = 'w-20' }: { width?: string; labelW?: string }) => (
    <div className={`${width} flex flex-col gap-1`}>
      <div className={`${bar} h-3 ${labelW}`} />
      <div className={`${bar} h-[34px] w-full`} />
    </div>
  );

  return (
    <div
      className="flex flex-col gap-4"
      role="status"
      aria-busy="true"
      aria-label="Starting import…"
    >
      {/* Smart fill is intentionally gone while submitting — fields only. */}

      {/* Row 1: client name + source system */}
      <div className="flex gap-4">
        <Field />
        <Field />
      </div>

      {/* Row 2: host + port + protocol */}
      <div className="flex gap-3">
        <Field />
        <Field width="w-24" labelW="w-8" />
        <Field width="w-28" labelW="w-14" />
      </div>

      {/* Row 3: credentials */}
      <div className="flex gap-3">
        <Field />
        <Field />
      </div>

      {/* Submit button */}
      <div className="flex justify-end pt-1">
        <div className={`${bar} h-8 w-28`} />
      </div>
    </div>
  );
}
